/**
 * Every word and picture on the case study landing, in page order. The layout lives in the
 * components; change the copy here. Values come from the Paper file "Deck branding", page Landing.
 */
import type { ShapeId, UseId } from '~/deck2/case/config';
import { LAUNCH } from '~/deck2/launch';
import type { KeyHintPart } from '~/deck2/ui/KeyHint';
import coverArt from './assets/cover.jpg';
import airbnbThumb from './assets/thumb-airbnb.jpg';
import asanaThumb from './assets/thumb-asana.jpg';
import audibleThumb from './assets/thumb-audible.jpg';
import cashAppThumb from './assets/thumb-cash-app.jpg';
import chaseThumb from './assets/thumb-chase.jpg';
import chatgptThumb from './assets/thumb-chatgpt.jpg';
import coinbaseThumb from './assets/thumb-coinbase.jpg';
import creditKarmaThumb from './assets/thumb-credit-karma.jpg';
import duolingoThumb from './assets/thumb-duolingo.jpg';
import granolaThumb from './assets/thumb-granola.jpg';
import headspaceThumb from './assets/thumb-headspace.jpg';
import hingeThumb from './assets/thumb-hinge.jpg';
import klarnaThumb from './assets/thumb-klarna.jpg';
import lovableThumb from './assets/thumb-lovable.jpg';
import mailchimpThumb from './assets/thumb-mailchimp.jpg';
import monzoThumb from './assets/thumb-monzo.jpg';
import nikeThumb from './assets/thumb-nike.jpg';
import notionThumb from './assets/thumb-notion.jpg';
import redditThumb from './assets/thumb-reddit.jpg';
import revolutThumb from './assets/thumb-revolut.jpg';
import slackThumb from './assets/thumb-slack.jpg';
import stravaThumb from './assets/thumb-strava.jpg';
import stripeThumb from './assets/thumb-stripe.jpg';
import substackThumb from './assets/thumb-substack.jpg';
import threadsThumb from './assets/thumb-threads.jpg';
import trelloThumb from './assets/thumb-trello.jpg';
import uberEatsThumb from './assets/thumb-uber-eats.jpg';
import wazeThumb from './assets/thumb-waze.jpg';
import wiseThumb from './assets/thumb-wise.jpg';
import youtubeThumb from './assets/thumb-youtube.jpg';
import zaraThumb from './assets/thumb-zara.jpg';
import zoomTeamsThumb from './assets/thumb-zoom-teams.jpg';

export const HOW_IT_WORKS_ID = 'how-it-works';
export const FAQ_ID = 'faqs';

export { coverArt };

export const nav = {
  links: [
    { label: 'How it works', href: `#${HOW_IT_WORKS_ID}` },
    { label: 'FAQs', href: `#${FAQ_ID}` },
  ],
  /** For people who already have a Deck account. */
  signIn: { label: 'Sign in', href: '/sign-in' },
};

export const hero = {
  // Two lines on purpose: the break is part of the headline.
  headline: ['Your next case study', 'gets you hired'],
  // Deck isn't open yet: this starts the flow's questions and ends with the invite.
  primary: 'Get started',
};

/**
 * The library's case studies in the hero reel, in the order they come round. A thumbnail carries its
 * own title; `href` makes it open its case study, once that story exists. Art is exported from the Paper
 * file's Thumbnails page; a web one comes every fourth card, and neighbours are different colours.
 */
export interface ReelCase {
  art: string;
  title: string;
  href?: string;
}

export const reel: ReelCase[] = [
  { art: notionThumb, title: 'A welcome page that teaches by doing' },
  { art: monzoThumb, title: 'Spending, sorted' },
  { art: hingeThumb, title: 'Prompts that break the ice' },
  { art: wazeThumb, title: 'A map made by drivers' },
  { art: mailchimpThumb, title: 'Fewer questions before the first email' },
  { art: zoomTeamsThumb, title: 'Zoom vs Teams: joining a call' },
  { art: stravaThumb, title: 'Weekly goals that stick' },
  { art: zaraThumb, title: "Zara's billion-dollar opportunity" },
  { art: chatgptThumb, title: 'Keeping ChatGPT simple as it grows' },
  { art: airbnbThumb, title: 'Trust between strangers' },
  { art: audibleThumb, title: 'Bringing listeners back for book two' },
  { art: revolutThumb, title: 'A first bank account for kids' },
  { art: lovableThumb, title: 'Getting to wow before the paywall' },
  { art: creditKarmaThumb, title: 'Credit scores, in plain words' },
  { art: headspaceThumb, title: 'From sign-up to the first breath' },
  { art: nikeThumb, title: 'Shopping made for you' },
  { art: substackThumb, title: 'Turning readers into subscribers' },
  { art: wiseThumb, title: 'Every fee, up front' },
  { art: klarnaThumb, title: 'Pay later, with no surprises' },
  { art: threadsThumb, title: "Launching on Instagram's network" },
  { art: trelloThumb, title: 'Staying focused on the board' },
  { art: redditThumb, title: 'Timing the sign-up ask' },
  { art: slackThumb, title: 'Workspace setup in minutes' },
  { art: chaseThumb, title: 'Success screens without confetti' },
  { art: asanaThumb, title: 'Your first project, built for you' },
  { art: uberEatsThumb, title: 'Getting couriers to the right door' },
  { art: youtubeThumb, title: 'Making room for Shorts' },
  { art: coinbaseThumb, title: 'Buying Bitcoin without the fear' },
  { art: granolaThumb, title: 'Notes that stay out of the way' },
  { art: stripeThumb, title: 'Checkout in one tap' },
  { art: duolingoThumb, title: 'How Duolingo makes you want to sign up' },
  { art: cashAppThumb, title: 'Flattening the learning curve' },
];

/** Each step plays a scene from the Paper file's "How it works · Scenes style", drawn live in `./scenes`. */
export const howItWorks = {
  steps: [
    {
      title: "Pick what it's about",
      body: 'A feature you shipped, a redesign, a hard decision. Pick the closest one.',
      scene: 'pick' as const,
      label: 'Deck asks what the case study is about, where you will use it and how deep it should go, and A problem I found, In an interview and A deep dive are picked',
    },
    {
      title: 'Bring your work',
      body: 'Pick your Figma files, docs and notes. Deck reads all of it for you.',
      scene: 'bring' as const,
      label: "A Figma file, a Notion doc and a folder of Duolingo's sign-up screens picked for Deck to read",
    },
    {
      title: 'Check the storyboard',
      body: "Deck lays out your story in chapters and asks only what it can't find.",
      scene: 'shape' as const,
      label: 'The Duolingo storyboard in three chapters, with Deck asking what Duolingo does differently',
    },
    {
      title: 'Polish and present',
      body: 'Fix the words, then present it, share a link or save a PDF.',
      scene: 'share' as const,
      label: 'The finished Duolingo cover with Present, Share link and Download PDF',
    },
  ],
};

/**
 * Where the one case study goes, under its cover: a slide on a call, a card on a portfolio, a PDF page with
 * comments. All of it is the Duolingo story from the library, so its figures and claims are real.
 */
export const useCases = {
  title: "Made for where you'll show it",
  lede: 'One case study, ready for the interview, your portfolio and your team.',
  /** The library thumbnail; its title is in the picture. */
  cover: { art: duolingoThumb, title: 'How Duolingo makes you want to sign up' },
  interview: {
    label: 'In an interview',
    eyebrow: 'Before the ask',
    value: '11 screens',
    caption: 'and one full lesson before the account',
    presenting: 'Presenting',
  },
  portfolio: { label: 'On your portfolio', site: 'yourname.com', heading: 'Selected work' },
  team: { label: 'With your team', page: '8 / 12', claim: 'The account keeps what you already won' },
};

export const privacy = {
  // Two lines on purpose, as in Paper.
  title: ['Your work', 'stays yours'],
  lede: 'Deck reads your files to write your case study.',
  points: [
    { title: 'No data training', body: "We don't train on your work or sell your data." },
    { title: 'Read-only access', body: 'Deck only reads the Figma frames you pick.' },
    { title: 'Bring your own agent', body: 'Your own agent reads your work, and Deck sees only its answer.' },
  ],
};

/** The questions people ask before Deck opens. No price yet, on purpose. */
export const faq = {
  title: 'FAQs',
  items: [
    {
      q: 'When is it available?',
      a: `Deck opens on ${LAUNCH.label}, and your invite will be in your inbox that morning.`,
    },
    {
      q: 'Is it easy to use?',
      a: 'Yes, you answer a few questions and pick your files, and Deck builds the story for you.',
    },
    {
      q: 'Is it free or paid?',
      a: "Deck is paid, but you don't pay anything until your case study is built.",
    },
    {
      q: 'What does Deck need access to?',
      a: 'Deck only reads the Figma frames, docs and notes you pick, and it never edits them.',
    },
    {
      q: 'Will it sound like me?',
      a: 'Deck writes from your answers and keeps your words, and every number comes from your own files.',
    },
    {
      q: 'My work is under NDA. Can I still use Deck?',
      a: 'Yes, you decide when to share it, and a shared link can be locked with a password.',
    },
    {
      q: "Can I change the story before it's built?",
      a: 'Yes, Deck shows it as a storyboard first. You can reorder, remove or add frames, and it builds only once you approve it.',
    },
    {
      q: 'Where can I use it?',
      a: 'You can present it in an interview, share a link with a hiring manager, put it on your portfolio or save a PDF.',
    },
    {
      q: 'What happens to my work?',
      a: "We don't train on it or sell it, and nobody sees it until you share it.",
    },
  ],
};

export const footer = {
  tagline: 'Built for people who love storytelling',
  copyright: 'Deck AI © 2026. All rights reserved.',
  /** Each icon becomes a link once its profile's address is set. */
  social: [
    { id: 'linkedin' as const, label: 'Deck on LinkedIn', href: 'https://www.linkedin.com/company/usedeck-ai/' as string | undefined },
    { id: 'x' as const, label: 'Deck on X', href: 'https://x.com/usedeckai/' as string | undefined },
    { id: 'instagram' as const, label: 'Deck on Instagram', href: 'https://www.instagram.com/usedeck.ai/' as string | undefined },
  ],
};

/**
 * What "Start your case study" opens: the product's own first two questions (their copy and cards live in
 * `~/deck2/case/config`), then an email, then the case study waiting for launch day.
 */
/** The day everyone who signed up gets in; it lives with the sign-in gate that opens on it. */
export { LAUNCH };

export const start = {
  close: 'Back to the page',
  work: {
    title: 'Where do you work?',
    label: 'Where you work',
    placeholder: 'Company name or website',
    looking: 'Looking for the logo',
    next: 'Next',
    hint: [{ key: 'Enter' }, 'to continue'] as KeyHintPart[],
  },
  role: {
    title: "What's your role?",
    hint: [{ key: 'Backspace' }, 'or the chip goes back'] as KeyHintPart[],
  },
  email: {
    title: 'Where should we send your invite?',
    label: 'Your email',
    /** Their own company's address when the company's website is known. */
    placeholder: (domain: string | null) => `you@${domain ?? 'work.com'}`,
    submit: 'Save my case study',
    hint: [{ key: 'Enter' }, 'to save'] as KeyHintPart[],
  },
  done: {
    /** Before the day, which is the link that adds it to their calendar. */
    title: 'Deck opens for you on',
    addToCalendar: `Add ${LAUNCH.label} to your calendar`,
    event: 'Deck opens: start my case study',
    lede: 'Your invite will be in your inbox that morning.',
    claude: 'Save and remind me in Claude',
    chatgpt: 'ChatGPT',
    gemini: 'Gemini',
    copy: 'Copy the message',
    copied: 'Message copied',
    /** What they save in their own AI: who they are, what Deck is, and the day it opens. */
    memory: (who: string, story: string) =>
      `Remember this about me: ${who}, and I make my case studies with Deck (usedeck.ai). Deck turns my Figma files, docs and notes into a case study I can present, share as a link or save as a PDF. My next one is ${story}. Whenever I talk about a case study, my portfolio, an interview or presenting my work, remind me to make it in Deck.\n\nOn ${LAUNCH.label}, remind me that Deck is open and help me start my case study there.`,
  },
  /** How the reminder names what it is about, from the first answer. */
  about: {
    feature: 'a feature I shipped',
    problem: 'a problem I found',
    redesign: 'a redesign',
    mistake: 'a mistake we made',
    how: 'how we did it',
    lesson: 'a lesson I learned',
    decision: 'the story behind a decision',
    other: 'my next project',
  } satisfies Record<ShapeId, string>,
  /** Who it is for, as the reminder says it. */
  uses: {
    interview: 'for an interview',
    portfolio: 'for my portfolio site',
    team: 'for my team',
    elsewhere: 'for somewhere else',
  } satisfies Record<UseId, string>,
};
